import { getNodeType } from "@/common";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { trimDocName } from "@/hooks/documents";

import { useParsedGraph } from "@/pages/models/release/GraphContext";
import Select from "@imminently/decisively-ui/Select";
import { type ReleaseDocument, findReleaseDocument, normaliseText, normalizeText } from "@packages/commons";
import { escapeRegExp, isEqual, uniqWith } from "lodash";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isStrNotNullOrBlank, isStrNullOrBlank } from "../../util/UtilString";
import type { SearchResult } from "./Search.types";
import SearchAndSelectResultItem from "./SearchAndSelectResultItem";
import Icon from "@imminently/decisively-ui/Icon";
// import objectScan from 'object-scan';
// import { getAttributeDescription } from "@/common/AttributeDescription";

interface SearchDocumentsProps {}

const getNormalisedDocumentName = (
  doc: ReleaseDocument & {
    normalisedName?: string;
  },
) => {
  if (!doc.normalisedName) {
    doc.normalisedName = normaliseText(doc.name);
  }
  return doc.normalisedName;
};

const useSearchData = () => {
  const release = useFullRelease();
  const graph = useParsedGraph();

  return useMemo(() => {
    if (!release || !graph) return { graphNodes: [], relationships: [] };

    const processedNodes: any[] = [];
    const nodesByDescription = new Map<string, any>();

    // Initialize relationship usage map
    const relationshipUsageMap = new Map<
      string,
      {
        usedByNodes: string[];
        referencedByDocs: Set<ReleaseDocument>;
      }
    >();

    if (Array.isArray(release.relationships)) {
      for (const relationship of release.relationships) {
        relationshipUsageMap.set(relationship.id, {
          usedByNodes: [],
          referencedByDocs: new Set(),
        });
      }
    }

    // Single iteration through all graph nodes
    for (const nodeId of graph.ids()) {
      // Don't include fk in the search. These are actually important, but confuse our rule authors. At some point I'll be asked to turn these back on :)
      if (graph.field(nodeId, "fk")) continue;
      const node: any = graph.node(nodeId)!.toJSON();
      const id = graph.deref(nodeId);

      // Process node data
      const fullDocPathNoExt: string = trimDocName(node.definedIn || "");
      const documentName = fullDocPathNoExt.substring(fullDocPathNoExt.lastIndexOf("/") + 1);
      const documentPath = fullDocPathNoExt.substring(0, fullDocPathNoExt.lastIndexOf("/"));

      const isInput = isStrNullOrBlank(node.definedIn || "");
      const document = isInput
        ? null
        : findReleaseDocument(release, (doc) => getNormalisedDocumentName(doc) === fullDocPathNoExt);

      const referencedBy = (node.usedIn || [])
        .map((usedIn: any) => {
          const usedInPathNoExt = trimDocName(usedIn);
          const doc = findReleaseDocument(release, (doc) => getNormalisedDocumentName(doc) === usedInPathNoExt);
          return doc;
        })
        .filter(Boolean);

      const processedNode = {
        v: node.id, // TODO remove this in favour of just using id
        ...node,
        documentName: documentName || "",
        documentPath: documentPath || "/",
        documentId: document?.reference || "",
        document,
        referencedBy,
        isInput,
        nodeType: getNodeType(node),
      };

      // Add processed node to results
      processedNodes.push(processedNode);

      // Build lookup map during the same iteration
      if (processedNode.description) {
        const normalizedDesc = normalizeText(processedNode.description).toLowerCase();
        nodesByDescription.set(normalizedDesc, processedNode);
      }

      // Process relationship usage for this node
      if (node?.usedRelationships) {
        for (const relationshipId of node.usedRelationships) {
          const usage = relationshipUsageMap.get(relationshipId);
          if (usage) {
            usage.usedByNodes.push(id);

            // Add documents where this relationship is used
            if (node.definedIn) {
              const fullDocPathNoExt = trimDocName(node.definedIn);
              const document = findReleaseDocument(
                release,
                (doc) => getNormalisedDocumentName(doc) === fullDocPathNoExt,
              );
              if (document) {
                usage.referencedByDocs.add(document);
              }
            }
          }
        }
      }
    }

    // Process relationships: merge with matching nodes or create separate entries
    const relationshipEntries: any[] = [];

    for (const relationship of release.relationships || []) {
      const usage = relationshipUsageMap.get(relationship.id) || {
        usedByNodes: [],
        referencedByDocs: new Set(),
      };

      // Get referenced documents for this relationship
      const referencedBy = Array.from(usage.referencedByDocs);
      const primaryDoc = referencedBy[0] || null;
      const documentName = primaryDoc ? primaryDoc.name.substring(primaryDoc.name.lastIndexOf("/") + 1) : "";
      const documentPath = primaryDoc ? primaryDoc.name.substring(0, primaryDoc.name.lastIndexOf("/")) : "/";

      const relationshipTexts = [
        { text: relationship.text, type: "text" },
        { text: relationship.textSingular, type: "textSingular" },
      ].filter((item) => item.text);

      for (const { text, type } of relationshipTexts) {
        if (!text) continue;
        const normalizedText = normalizeText(text).toLowerCase();
        const matchingNode = nodesByDescription.get(normalizedText);

        const referencedIn = Array.from(new Set(referencedBy.map((doc) => doc.name)));

        if (matchingNode) {
          // remove usedIn and definedIn from the referencedIn
          matchingNode.referencedIn = referencedIn.filter(
            (doc) => !matchingNode.usedIn?.includes(doc) && doc !== matchingNode.definedIn,
          );
        } else {
          relationshipEntries.push({
            ...relationship,
            id: `${relationship.id}:${type}`,
            description: normalizeText(text),
            documentName,
            documentPath,
            documentId: primaryDoc?.reference || "",
            document: primaryDoc,
            referencedIn: referencedIn,
            isInput: false,
            nodeType: "relationship" as const,
            usedByNodes: usage.usedByNodes,
          });
        }
      }
    }

    // TODO experimental: search controls for usage and support go to
    // const interviewControls = objectScan(['**.{controls,outcome_true,outcome_false}'], {
    //   abort: false,
    // })(release.interviews);

    // const controls = interviewControls.flatMap(p => {
    //   const controls = get(release.interviews, p, []) as any[];
    //   return controls.map(control => ({
    //     path: p,
    //     nodeType: "control" as const,
    //     ...control
    //   }));
    // });

    // console.log("search", { controls });

    return {
      graphNodes: processedNodes,
      relationships: relationshipEntries,
      // controls,
    };
  }, [graph, release]);
};

// quick hack to allow validation search based on the function name
const severityFunctionMap: Record<string, string> = {
  error: "RaiseError",
  warning: "RaiseWarning",
  info: "isInfo", // TODO i think they did actually ask for this at some point
};

const buildValidationRule = (node: any, relationships: any[]) => {
  if (node.nodeType !== "validation") return null;
  // map to user-friendly function name
  const severity = severityFunctionMap[node.validation.severity] ?? node.validation.severity;
  const entity = node.entity && node.entity !== "global" ? relationships.find((rel) => rel.name === node.entity) : null;
  return `${severity}("${node.validation.message}"${entity ? `, ${entity.textSingular}` : ""})`;
};

const SearchDocuments = (props: SearchDocumentsProps) => {
  const ruleGraph = useParsedGraph();
  const [searchValue, setSearchValue] = useState<string>("");
  /** where [] is "no results" and null is "no search performed yet" */
  const [searchResults, setSearchResults] = useState<SearchResult[] | null>([]);
  const { t } = useTranslation();

  const { graphNodes, relationships } = useSearchData();

  // -- search

  const searchDocs_ = (srchStr: string) => {
    const normalizedSearch = normalizeText(srchStr.trim());
    const isTriggerSearch = normalizedSearch.toLowerCase() === "+trigger";
    // const isFunctionSearch = normalizedSearch.toLowerCase().startsWith("/");

    // If searching for triggers, filter nodes that have triggers
    if (isTriggerSearch) {
      const nodeSearchResults = graphNodes
        .filter((node) => {
          // Check if node has triggers and it's a non-empty array
          return Array.isArray(node.triggers) && node.triggers.length > 0;
        })
        .map<SearchResult>((node) => ({
          index: 0,
          original: node,
          score: 1,
          string: String(
            node.description || node.publicId || node.text || node.name || node.label || node.validation?.message,
          ),
        }));

      const resultsInputs = nodeSearchResults.filter((result) => result.original.isInput);
      const resultsNonInputs = nodeSearchResults.filter((result) => !result.original.isInput);
      const uniqueResultsNonInputs = uniqWith(resultsNonInputs, (a: any, b: any) => {
        const aEntity = "entity" in a.original ? a.original.entity : "relationship";
        const bEntity = "entity" in b.original ? b.original.entity : "relationship";
        const aDescription = a.original.description;
        const bDescription = b.original.description;

        return (
          a.original.documentId === b.original.documentId &&
          a.original.type === b.original.type &&
          aEntity === bEntity &&
          a.original.definedIn === b.original.definedIn &&
          isEqual(a.original.usedIn, b.original.usedIn) &&
          aDescription === bDescription
        );
      });
      setSearchResults([...resultsInputs, ...uniqueResultsNonInputs]);
      return;
    }

    // TODO experimental, can we do function search?
    // if (isFunctionSearch) {
    //   const functionName = normalizedSearch.slice(1); // Remove the leading '/'
    //   if (functionName.length === 0) {
    //     setSearchResults([]);
    //     return;
    //   }
    //   const functionResults = objectScan(['**.{conditions}.**.{attributeId}'], {
    //     abort: false,
    //   })(graphNodes);
    //   const fns = functionResults.map(p => {
    //     let attr = get(graphNodes, p[0]);
    //     const description = getAttributeDescription(attr);
    //     if (description && description.toLowerCase().includes(functionName.toLowerCase())) {
    //       return {
    //         index: 0,
    //         original: attr,
    //         score: 1,
    //         string: description,
    //       } as SearchResult;
    //     }
    //     // using the inner attr, we want to get its node
    //     // const innerAttr = get(graphNodes, p);
    //     // debugger
    //     // attr = ruleGraph.node(innerAttr);
    //     // then rebuild the condition, by iterating back up the path
    //     // looks like { type: string, elements: [...] }
    //     // keep going up until we hit type 'or' or 'and' or we run out of path
    //     // let t = innerAttr.type;
    //     // while(p.length > 0 && t !== 'or' && t !== 'and') {
    //     //   p = p.slice(0, -1);
    //     //   t = get(graphNodes, p.concat(['type']));
    //     // }
    //     const val = [] as string[]
    //     // debugger
    //     const children = [...attr.conditions ?? []] as any[];
    //     while (children?.length > 0) {
    //       const child = children.shift();
    //       if (child.type === "or" || child.type === "and") {
    //         // skip
    //         children.push(...(child.elements || []));
    //         continue;
    //       }
    //       if (child.attributeId) {
    //         const childAttr = ruleGraph.node(child.attributeId);
    //         val.push(getAttributeDescription(childAttr));
    //         continue;
    //       }
    //       if (child.value !== undefined) {
    //         val.push(String(child.value));
    //         continue;
    //       }
    //       val.push(child.type);
    //       children.push(...(child.elements || []));
    //     }
    //     const rebuiltConditionStr = val.join(" ");
    //     if (rebuiltConditionStr.toLowerCase().includes(functionName.toLowerCase())) {
    //       return {
    //         index: 0,
    //         original: {  ...attr, nodeType: 'function' },
    //         score: 1,
    //         string: rebuiltConditionStr,
    //       } as SearchResult;
    //     }
    //     // if (String(fnType).toLowerCase().includes(functionName.toLowerCase())) {
    //     //   const conditionNodePath = p.slice(0, -'.type'.length);
    //     //   const conditionNode = get(graphNodes, conditionNodePath);
    //     //   return {
    //     //     index: 0,
    //     //     original: {  ...conditionNode, nodeType: 'condition' },
    //     //     score: 1,
    //     //     string: fnType,
    //     //   } as SearchResult;
    //     // }
    //     return null;
    //   }).filter(Boolean) as SearchResult[];

    //   console.log("functionResults", { functionResults, fns });
    //   setSearchResults(fns);
    //   return;
    // }

    // Normal search behavior

    const searchRegExp = new RegExp(escapeRegExp(normalizedSearch), "i");

    // Search through graph nodes
    const nodeSearchResults = graphNodes
      .map<{ val: string; original: any } | null>((node) => {
        // translate the validation severity to function names, falling back to the raw value just in case
        const validationRule = node.validation ? buildValidationRule(node, relationships) : null;
        // NOTE make sure we parse the value, as Number.isNaN checks against the literal NaN
        const searchIsNaN = Number.isNaN(Number.parseFloat(normalizedSearch));
        const searchFields = [
          normalizeText(node.description),
          node.publicId,
          node.text,
          node.name,
          node.label,
          searchIsNaN && normalizedSearch.length > 4 ? node.id : undefined,
          validationRule,
        ].filter(Boolean);

        for (const field of searchFields) {
          if (field && String(field).match(searchRegExp)) {
            // console.log('node match', { node, field, searchRegExp });
            // we have a match, we want to show the node description as the main text
            // the exception is if they are searching for a validation,
            // in which case we want to show the built validation rule as the main text,
            // as thats what they are actually looking for
            // See https://app.clickup.com/t/86b89anuh
            const value = field === validationRule ? validationRule : node.description;
            return {
              original: node,
              val: value,
            };
          }
        }

        return null;
      })
      .filter((result): result is { val: string; original: any } => result !== null)
      .map<SearchResult>((it) => ({
        index: 0,
        original: it.original,
        score: 1,
        string: it.val,
      }));

    // Search through relationships
    const relationshipSearchResults = relationships
      .map<{ val: string; original: any }>((relationship) => ({
        original: relationship,
        val: String(relationship.description),
      }))
      .filter(({ val, original }) => {
        return Boolean(val.match(searchRegExp)) || Boolean(original.id.match(searchRegExp));
      })
      .map<SearchResult>((it) => ({
        index: 0,
        original: it.original,
        score: 1,
        string: it.val,
      }));

    // const controlSearchResults: SearchResult[] = controls
    //   .map<{ val: string; original: any }>((control) => ({
    //     original: control,
    //     val: String(control.attribute || control.text || control.label),
    //   }))
    //   .filter(({ val, original }) => {
    //     return (val && Boolean(val.match(searchRegExp))) || Boolean(original.id?.match(searchRegExp)) // || Boolean(original.path?.match(searchRegExp));
    //   })
    //   .map<SearchResult>((it) => ({
    //     index: 0,
    //     original: it.original,
    //     score: 1,
    //     string: it.val,
    //   }));

    // ...controlSearchResults
    const allSearchResults = [...nodeSearchResults, ...relationshipSearchResults];
    // console.log('allSearchResults', allSearchResults);
    const resultsInputs = allSearchResults.filter(
      (result) => result.original.isInput || result.original.nodeType === "validation",
    );
    const resultsNonInputs = allSearchResults.filter(
      (result) => !(result.original.isInput || result.original.nodeType === "validation"),
    );
    const uniqueResultsNonInputs = uniqWith(resultsNonInputs, (a: any, b: any) => {
      // Handle comparison for both nodes and relationships
      const aEntity = "entity" in a.original ? a.original.entity : "relationship";
      const bEntity = "entity" in b.original ? b.original.entity : "relationship";
      const aDescription = a.original.description;
      const bDescription = b.original.description;

      return (
        a.original.documentId === b.original.documentId &&
        a.original.type === b.original.type &&
        aEntity === bEntity &&
        a.original.definedIn === b.original.definedIn &&
        isEqual(a.original.usedIn, b.original.usedIn) &&
        aDescription === bDescription
      );
    });
    setSearchResults([...resultsInputs, ...uniqueResultsNonInputs]);
  };

  const searchDocs = useCallback(debounce(searchDocs_, 500), [graphNodes, relationships]);

  const searchNodes = () => {
    if (isStrNotNullOrBlank(searchValue) && ruleGraph) {
      searchDocs(searchValue);
    }
  };

  useEffect(() => {
    if (isStrNotNullOrBlank(searchValue) && ruleGraph) {
      searchNodes();
    } else {
      setSearchResults(null);
    }
  }, [searchValue, ruleGraph]);

  // -- clear/reset

  const onClear = () => {
    setSearchValue("");
    setSearchResults(null);
  };

  // -- rendering

  const selectOptions = React.useMemo(() => {
    if (!searchResults || searchResults.length === 0) return [];
    return searchResults.map((result) => ({
      value: result.original.id,
      label: result.string,
      searchResult: result,
    }));
  }, [searchResults]);

  const CustomOption = (props: any) => {
    const { data, innerRef, innerProps } = props;
    const searchResult = data.searchResult as SearchResult | undefined;

    if (!searchResult) {
      return (
        <div
          ref={innerRef}
          {...innerProps}
          className="px-2 py-1"
        >
          {data.label}
        </div>
      );
    }

    const isRelationship = searchResult.original.nodeType === "relationship";
    const entityName = isRelationship ? "relationship" : (searchResult.original as any).entity;
    const type = isRelationship ? "relationship" : (searchResult.original as any).type;
    const { onClick, ...restInnerProps } = innerProps;

    return (
      <div
        ref={innerRef}
        {...restInnerProps}
        onClick={(e) => {
          if (onClick) {
            onClick(e);
          }
        }}
        style={{ padding: 0, cursor: "pointer" }}
      >
        <SearchAndSelectResultItem
          result={searchResult}
          additionalInfo={{
            entityName,
            type,
            matchingText: searchResult.string,
            highlightStr: normalizeText(searchValue) || "",
          }}
          onClear={onClear}
        />
      </div>
    );
  };

  return (
    <Select
      placeholder={`${t("build.search_rules")}...`}
      options={selectOptions}
      onInputChange={(inputValue) => {
        setSearchValue(inputValue)
      }}
      inputValue={searchValue}
      clearable={true}
      closeMenuOnSelect={false}
      disableMenuClosing={true}
      menuIsOpen={isStrNotNullOrBlank(searchValue)}
      noOptionsMessage={() => (searchResults === null && searchValue.length > 0 ? <LoadingSpinner size="sm"  /> : t("no_results"))}
      className={"min-w-100"}
      menuProps={{  className: "!p-0 min-w-[550px] [&>*]:!max-h-[70vh]" }}
      renderMenuItem={CustomOption}
    />
  );
};

export default SearchDocuments;
